import { Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./auth-context";
import { SignInPage } from "./pages/SignIn";
import { OnboardingPage } from "./pages/Onboarding";
import { Shell } from "./components/Shell";
import { OverviewPage } from "./pages/Overview";
import { EventsShell } from "./pages/EventsShell";
import { PoliciesShell } from "./pages/PoliciesShell";
import { UsersShell } from "./pages/UsersShell";
import { ProtectionsShell } from "./pages/ProtectionsShell";
import { SettingsPage } from "./pages/Settings";
import { ApiAccessPage } from "./pages/ApiAccess";
import { BillingPage } from "./pages/Billing";
import { GrowthShell } from "./pages/Growth/GrowthShell";
import { GrowthDashboard } from "./pages/Growth/GrowthDashboard";
import { GrowthLeads } from "./pages/Growth/GrowthLeads";
import { GrowthPipeline } from "./pages/Growth/GrowthPipeline";
import { GrowthSuppression } from "./pages/Growth/GrowthSuppression";
import { GrowthSettings as GrowthSettingsPage } from "./pages/Growth/GrowthSettings";
import {
  GrowthOutreachPage,
  GrowthRepliesPage,
  GrowthCallsPage,
  GrowthPilotsPage,
  GrowthReportsPage,
} from "./pages/Growth/GrowthPlaceholder";

export default function App() {
  return (
    <AuthProvider>
      <Routes>
        <Route path="/sign-in" element={<SignInPage />} />
        <Route path="/signin" element={<Navigate to="/sign-in" replace />} />
        <Route path="/onboarding" element={<Gate requireNoWorkspace><OnboardingPage /></Gate>} />
        <Route element={<Gate requireWorkspace><Shell /></Gate>}>
          <Route path="/" element={<OverviewPage />} />
          <Route path="/events" element={<EventsShell />} />
          <Route path="/false-flags" element={<EventsShell />} />
          <Route path="/policies" element={<PoliciesShell />} />
          <Route path="/policies/packs" element={<PoliciesShell />} />
          <Route path="/protections" element={<ProtectionsShell />} />
          <Route path="/domains" element={<ProtectionsShell />} />
          <Route path="/tracked-names" element={<ProtectionsShell />} />
          <Route path="/users" element={<UsersShell />} />
          <Route path="/teams" element={<UsersShell />} />
          <Route path="/settings" element={<SettingsPage />} />
          <Route path="/billing" element={<BillingPage />} />
          <Route path="/api-access" element={<ApiAccessPage />} />

          {/* Hidden Growth OS — super_admin only, gated client-side by
              GrowthShell and server-side by every callable. Not in the
              main sidebar; reachable by URL or via the profile menu. */}
          <Route path="/admin/growth" element={<GrowthShell />}>
            <Route index element={<GrowthDashboard />} />
            <Route path="leads" element={<GrowthLeads />} />
            <Route path="pipeline" element={<GrowthPipeline />} />
            <Route path="outreach" element={<GrowthOutreachPage />} />
            <Route path="replies" element={<GrowthRepliesPage />} />
            <Route path="calls" element={<GrowthCallsPage />} />
            <Route path="pilots" element={<GrowthPilotsPage />} />
            <Route path="reports" element={<GrowthReportsPage />} />
            <Route path="suppression" element={<GrowthSuppression />} />
            <Route path="settings" element={<GrowthSettingsPage />} />
          </Route>
        </Route>
      </Routes>
    </AuthProvider>
  );
}

function Gate({
  children,
  requireWorkspace,
  requireNoWorkspace,
}: {
  children: React.ReactNode;
  requireWorkspace?: boolean;
  requireNoWorkspace?: boolean;
}) {
  const { user, profile, loading, isDemo } = useAuth();
  if (loading) return <div className="sg-auth-shell"><div className="sg-auth-card">Loading…</div></div>;
  if (!user) return <Navigate to="/sign-in" replace />;

  // Demo users already have a workspace, but we want to walk them through
  // the onboarding tour once per session so the flow can be shown to clients.
  // A sessionStorage flag is set when they Skip or Complete.
  const demoSeenOnboarding = isDemo && sessionStorage.getItem("vt_demo_onboarding_seen") === "1";
  const demoWantsOnboarding = isDemo && !demoSeenOnboarding;

  if (requireWorkspace && !profile.workspace) return <Navigate to="/onboarding" replace />;
  if (requireWorkspace && demoWantsOnboarding) return <Navigate to="/onboarding" replace />;
  if (requireNoWorkspace && profile.workspace && !demoWantsOnboarding) {
    return <Navigate to="/" replace />;
  }
  return <>{children}</>;
}
